/**
 * Type guards and utilities for safe error handling
 */

interface AxiosErrorResponse {
  response?: {
    data?: {
      error?: string;
      details?: string | Record<string, unknown>;
    };
  };
  message?: string;
}

/**
 * Type guard to check if an error has an Axios-like structure
 */
export function isAxiosError(error: unknown): error is AxiosErrorResponse {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false;
  }

  const errorWithResponse = error as { response?: unknown };
  return typeof errorWithResponse.response === 'object';
}

/**
 * Type guard to check if error details is an object (validation errors)
 */
export function isValidationErrorDetails(details: unknown): details is Record<string, unknown> {
  return typeof details === 'object' && details !== null && !Array.isArray(details);
}

/**
 * Safely extract error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Check for Axios error structure
  if (isAxiosError(error)) {
    const responseData = error.response?.data;

    // Try to get error message from various possible locations
    if (responseData?.error) {
      return responseData.error;
    }

    if (typeof responseData?.details === 'string') {
      return responseData.details;
    }
  }

  // Check for standard Error object
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Safely extract validation error details from error object
 */
export function getValidationErrorDetails(error: unknown): Record<string, unknown> | null {
  if (!isAxiosError(error)) {
    return null;
  }

  const details = error.response?.data?.details;

  if (isValidationErrorDetails(details)) {
    return details;
  }

  return null;
}
